import * as React from 'react';

import { withIntl, IntlProps } from 'ha/i18n';
import * as SearchFilters from 'ha/types/SearchFilters';
import formatPrice from 'ha/utils/formatPrice';

import FilterPopover, { ButtonElementType } from './FilterPopover';
import PriceFilter from './PriceFilter';
import { WidthType } from 'ha/components/Filters/FilterPopover';

export interface Props {
  searchParams: {};
  onChangeFilters: SearchFilters.FilterSubmit;
  loadPreviewNumberOfListings: (searchParams: {}) => void;
  clearPreviewNumberOfListings: () => void;
  priceFilter: SearchFilters.PriceFilterProps;
  button: ButtonElementType;
  toggleFilterPopover: (open: boolean) => void;
  updateButtonText: string;
  priceFilterTitle: string;
  width?: WidthType;
}

interface State {
  price: SearchFilters.PriceFilterChange | null;
}
class PriceFilterDesktop extends React.Component<Props & IntlProps, State> {
  state: State;

  constructor(props: Props & IntlProps) {
    super(props);

    this.state = this.getStateFromProps();
  }

  getStateFromProps = () => ({
    price:
      this.props.priceFilter.selectedMin || this.props.priceFilter.selectedMax
        ? {
            min: this.props.priceFilter.selectedMin,
            max: this.props.priceFilter.selectedMax,
            currency: this.props.priceFilter.currency,
          }
        : null,
  });

  handleUpdate = () => {
    const { onChangeFilters, priceFilter } = this.props;
    const { price } = this.state;
    const rate = this.getCurrencyRate();

    onChangeFilters({
      price: {
        currency: price ? price.currency : priceFilter.currency,
        min: price ? Math.floor(price.min * rate) : null,
        max: price ? Math.floor(price.max * rate) : null,
      },
    });
  };

  handleOpen = () => {
    this.setState(this.getStateFromProps());
  };

  handleClose = () => {
    this.props.clearPreviewNumberOfListings();
  };

  handleReset = () => {
    this.updatePrice(null);
  };

  handlePriceFilterChange = (price: SearchFilters.PriceFilterChange) => {
    const { min: selectedMin, max: selectedMax, currency } = price;

    const {
      priceMin,
      trimmedPriceMin,
      priceMax,
      trimmedPriceMax,
    } = this.props.priceFilter;

    // If selected value is the same as trimmed value (max or min possible) use non-trimmed value.
    const min = selectedMin !== trimmedPriceMin ? selectedMin : priceMin;
    const max = selectedMax !== trimmedPriceMax ? selectedMax : priceMax;

    this.updatePrice({ min, max, currency });
  };

  updatePrice = (price: SearchFilters.PriceFilterChange | null) => {
    this.setState({ price }, () => {
      const rate = this.getCurrencyRate();
      this.props.loadPreviewNumberOfListings({
        ...this.props.searchParams,
        priceMin: price && price.min ? Math.floor(price.min * rate) : null,
        priceMax: price && price.max ? Math.floor(price.max * rate) : null,
        currency: price ? price.currency : this.props.priceFilter.currency,
      });
    });
  };

  getCurrencyRate = (): number => {
    const {
      priceFilter: { currencyRates, currency: from },
    } = this.props;
    const to = (this.state.price && this.state.price.currency) || from;

    const fromRate = currencyRates[from];
    const toRate = currencyRates[to];

    return toRate / fromRate;
  };

  priceLabelFormatter = (price: number): React.ReactNode => {
    const {
      currency: originalCurrency,
      trimmedPriceMax,
    } = this.props.priceFilter;
    const currency = this.state.price
      ? this.state.price.currency
      : originalCurrency;

    const rate = this.getCurrencyRate();
    const formatted = formatPrice(price * rate, currency);

    return price === trimmedPriceMax ? `${formatted}+` : formatted;
  };

  render() {
    const {
      priceFilter,
      intl: { T },
      button,
      toggleFilterPopover,
      updateButtonText,
      priceFilterTitle,
      width = WidthType.md,
    } = this.props;
    const { price } = this.state;

    const rate = this.getCurrencyRate();

    return (
      <FilterPopover
        button={button}
        updateButtonText={updateButtonText}
        resetButtonText={T('Reset price')}
        onUpdateButtonClick={this.handleUpdate}
        onResetButtonClick={this.handleReset}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        toggleFilterPopover={toggleFilterPopover}
        width={width}
      >
        <PriceFilter
          onPriceChange={this.handlePriceFilterChange}
          onCurrencyChange={this.handlePriceFilterChange}
          priceDistribution={priceFilter.trimmedPriceDistribution}
          priceMin={priceFilter.trimmedPriceMin}
          priceMax={priceFilter.trimmedPriceMax}
          currency={price ? price.currency : priceFilter.currency}
          selectedMin={price ? price.min : priceFilter.trimmedPriceMin}
          selectedMax={price ? price.max : priceFilter.trimmedPriceMax}
          rate={rate}
          priceLabelFormatter={this.priceLabelFormatter}
          priceFilterTitle={priceFilterTitle}
        />
      </FilterPopover>
    );
  }
}

export default withIntl(PriceFilterDesktop);
